/**
 * WhatsApp via `wa.me` deep links (#27), which is open question #1's documented
 * default: free, no business verification, no per-conversation charge.
 *
 * What this costs us is that nothing comes back — WhatsApp never tells us whether
 * the message was sent, read or replied to. So the rep logs the outcome, exactly
 * as they do for a call. That is the honest trade, and it is why the activity is
 * recorded through the same log_activity() path either way: if the paid Business
 * API is ever switched on, the webhook writes the same activity rows and nothing
 * downstream changes.
 */

/** Placeholders a template may use. Anything unknown is left alone rather than
 *  replaced with "undefined" — a half-filled message is embarrassing, a visibly
 *  unfilled one is just a template the rep can finish. */
export interface TemplateValues {
  client_name?: string | undefined;
  agent_name?: string | undefined;
  project?: string | undefined;
  unit_code?: string | undefined;
  price?: string | undefined;
  company?: string | undefined;
}

export function fillTemplate(body: string, values: TemplateValues): string {
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => {
    const value = values[key as keyof TemplateValues];
    return value && value.trim() ? value : match;
  });
}

/**
 * Builds the wa.me URL.
 *
 * The number must be digits only with no leading `+`: wa.me rejects the `+` and
 * silently opens a "start a chat" screen with nothing filled in, which looks to
 * the rep like the feature is broken rather than the number being wrong.
 */
export function whatsappUrl(phoneE164: string, message?: string): string {
  const digits = phoneE164.replace(/\D/g, '');
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/** One-tap dial, for the same reason (#28): a rep in a car should press once. */
export function telUrl(phoneE164: string): string {
  return `tel:${phoneE164}`;
}
